import { ReactNode } from 'react'

import { revalidateLogic } from '@tanstack/react-form'

import { useAppForm } from '@ui/components/forms'
import { FieldGroup } from '@ui/components/shadcn/ui/field'

import {
  useAuthAnalytics,
  useAuthScreenView,
} from '@web-app/components/auth/analytics'
import { AuthLegalNotice } from '@web-app/components/auth/AuthLegalNotice'
import { register } from '@web-app/lib/auth/client'
import { mapServerFieldErrors } from '@web-app/lib/errors/serverFieldErrors'
import {
  GENERIC_ERROR,
  transportFailureCopy,
} from '@web-app/lib/errors/transportFailure'
import {
  ClassifiedIdentifier,
  createAccountSchema,
  CreateAccountValues,
  PASSWORD_RULE_HINT,
} from '@web-app/schemas/auth'

const FIELD_NAMES = [
  'phone',
  'email',
  'password',
  'confirmPassword',
] as const satisfies ReadonlyArray<keyof CreateAccountValues>

interface Props {
  initialEmail?: string
  initialPhone?: string
  redirect?: string
  // The route answers where the new account goes next.
  onSuccess: (destination: string) => void | Promise<void>
  // The email or phone already belongs to an account; the caller hands the
  // user to sign-in.
  onExistingAccount: (identifier: ClassifiedIdentifier) => void
}

export function CreateAccountForm({
  initialEmail = '',
  initialPhone = '',
  redirect,
  onSuccess,
  onExistingAccount,
}: Props) {
  const { trackTap, trackMilestone } = useAuthAnalytics()
  useAuthScreenView('createAccount')

  const form = useAppForm({
    defaultValues: {
      phone: initialPhone,
      email: initialEmail,
      password: '',
      confirmPassword: '',
    } satisfies CreateAccountValues,
    validationLogic: revalidateLogic(),
    validators: {
      onDynamic: createAccountSchema,
      onSubmitAsync: async ({ value }) => {
        trackTap('createAccountNext')
        const outcome = await register({
          phone: value.phone,
          email: value.email,
          password: value.password,
          redirect,
        })
        if (outcome.kind === 'unreachable') {
          return { form: transportFailureCopy() }
        }
        if (outcome.kind === 'signed-in') {
          trackMilestone('registered')
          await onSuccess(outcome.destination)
          return null
        }

        if (!outcome.errors.length) {
          return { form: outcome.message ?? GENERIC_ERROR }
        }
        const { fields, orphans } = mapServerFieldErrors({
          errors: outcome.errors,
          fieldNames: FIELD_NAMES,
        })
        const taken = (['email', 'phone'] as const).find((name) =>
          /already registered/i.test(fields[name]?.message ?? ''),
        )
        // The error carries the sign-in action inline; switching flows stays
        // the user's click, never an automatic swap.
        const withConflict: Record<string, { message: ReactNode }> = taken
          ? {
              ...fields,
              [taken]: {
                message: (
                  <>
                    This {taken === 'email' ? 'email' : 'phone number'} already
                    belongs to an account.{' '}
                    <button
                      type="button"
                      className="underline"
                      onClick={() =>
                        onExistingAccount({ kind: taken, value: value[taken] })
                      }
                    >
                      Sign in
                    </button>
                  </>
                ),
              },
            }
          : fields
        return {
          fields: withConflict,
          ...(orphans.length ? { form: orphans.join(' ') } : {}),
        }
      },
    },
  })

  return (
    <form
      noValidate
      onSubmit={(event) => {
        event.preventDefault()
        event.stopPropagation()
        void form.handleSubmit()
      }}
    >
      <FieldGroup className="gap-2">
        <form.AppField name="phone">
          {(field) => <field.PhoneField required />}
        </form.AppField>
        <form.AppField name="email">
          {(field) => (
            <field.TextField
              label="Email"
              type="email"
              autoComplete="email"
              inputMode="email"
              required
            />
          )}
        </form.AppField>
        <form.AppField name="password">
          {(field) => (
            <field.PasswordField
              label="Password"
              autoComplete="new-password"
              description={PASSWORD_RULE_HINT}
              required
            />
          )}
        </form.AppField>
        <form.AppField name="confirmPassword">
          {(field) => (
            <field.PasswordField
              label="Confirm password"
              autoComplete="new-password"
              required
            />
          )}
        </form.AppField>
      </FieldGroup>
      <form.AppForm>
        <div className="mt-2 flex flex-col gap-2">
          <form.SubmitButton>Create account</form.SubmitButton>
          <form.FormError />
        </div>
      </form.AppForm>
      <AuthLegalNotice />
    </form>
  )
}
